import { useMemo, useState, useEffect, useCallback } from 'react';
import type {
  CohortWithDetails,
  CohortSessionTopic,
  CoachHuddle,
  FinalizationStatus,
  Topic,
  OneOnOneParticipant,
} from '../../../../types';
import { sessionsApi, type SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import { groupsApi, type GroupWithMembers } from '../../../../services/api/groups';
import { timeSlotsApi, type TimeSlotResponse } from '../../../../services/api/timeSlots';
import { topicsApi } from '../../../../services/api/topics';
import { sessionNumberConfigsApi } from '../../../../services/api/sessionNumberConfigs';
import { cohortEventsApi } from '../../../../services/api/cohortEvents';
import { naturalSortCompare } from '../../../../utils/formatters';
import type { GroupWithSessions } from './sessionsHelpers';
import { enabledCheckinCount } from './sessionsHelpers';

interface Modalities {
  hasGroupCoaching: boolean;
  hasOneOnOne: boolean;
  hasWorkshop: boolean;
  // MED-1350: the intro or midpoint check-in is on, or the cohort still has
  // live check-in rows (a booked check-in stays after both toggles go off).
  // Check-ins are 1:1 rows, so the 1:1 section shows for them even with no
  // 1:1 modality.
  hasCheckins: boolean;
}

interface SessionCounts {
  all: number;
  groupCoaching: number;
  oneOnOne: number;
  workshop: number;
}

export interface UseSessionsDataReturn {
  // Raw data
  groups: GroupWithMembers[];
  allSessions: SessionWithDetailsAndRsvp[];
  sessionTopics: CohortSessionTopic[];
  timeSlots: TimeSlotResponse[];
  finalizationStatus: FinalizationStatus | null;
  cohortHuddles: CoachHuddle[];
  oneOnOneParticipants: OneOnOneParticipant[];
  oneOnOneTotal: number;
  // MED-1350: cohort-wide count of participants with a paid 1:1 row
  oneOnOnePaidParticipantTotal: number;

  // Computed data
  groupsWithSessions: GroupWithSessions[];
  modalities: Modalities;
  sessionCounts: SessionCounts;
  filteredGroups: GroupWithSessions[];

  // UI state
  loading: boolean;
  hasLoaded: boolean;
  expandedGroups: Set<string>;
  showOneOnOnes: boolean;
  loadingMoreOneOnOne: boolean;

  // Actions
  loadData: () => Promise<void>;
  loadMoreOneOnOne: () => Promise<void>;
  toggleGroup: (groupId: string) => void;
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
  allGroupsExpanded: boolean;
  setShowOneOnOnes: (show: boolean) => void;
  setSessionTopics: React.Dispatch<React.SetStateAction<CohortSessionTopic[]>>;

  // Topic handling
  handleZeroStateTopicChange: (sessionNumber: number, topicId: string, topicName: string) => void;
  handleTopicSelectForSession: (
    topic: Topic,
    session: SessionWithDetailsAndRsvp,
    displayName?: string | null,
  ) => Promise<void>;

  // Huddle handling
  handleAddHuddle: (date: string | null) => Promise<void>;
  handleCohortEventRefresh: () => void;
  cohortEventsKey: number;
}

export function useSessionsData(
  cohort: CohortWithDetails,
  activeTab: string,
  groupFilter: string[],
  coachFilter: string[],
  isActive?: boolean,
  refreshKey?: number
): UseSessionsDataReturn {
  const [groups, setGroups] = useState<GroupWithMembers[]>([]);
  const [allSessions, setAllSessions] = useState<SessionWithDetailsAndRsvp[]>([]);
  const [sessionTopics, setSessionTopics] = useState<CohortSessionTopic[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlotResponse[]>([]);
  const [finalizationStatus, setFinalizationStatus] = useState<FinalizationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  // MED-1350: true after the first load ends. A reload keeps the content on
  // screen, so the page keeps its scroll and the 1:1 view choice.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showOneOnOnes, setShowOneOnOnes] = useState(true);
  const [cohortHuddles, setCohortHuddles] = useState<CoachHuddle[]>([]);
  const [cohortEventsKey, setCohortEventsKey] = useState(0);
  const [oneOnOneParticipants, setOneOnOneParticipants] = useState<OneOnOneParticipant[]>([]);
  const [oneOnOneTotal, setOneOnOneTotal] = useState(0);
  const [oneOnOnePaidParticipantTotal, setOneOnOnePaidParticipantTotal] = useState(0);
  // MED-1350: participants with a live check-in row, from the 1:1 list meta
  const [checkinParticipantTotal, setCheckinParticipantTotal] = useState(0);
  const [oneOnOnePage, setOneOnOnePage] = useState(1);
  const [loadingMoreOneOnOne, setLoadingMoreOneOnOne] = useState(false);

  // Check modalities for tab filtering
  // MED-1350: how many check-ins are on (0, 1, or 2)
  const checkinCount = enabledCheckinCount(cohort);

  const modalities = useMemo((): Modalities => {
    const result: Modalities = {
      hasGroupCoaching: false,
      hasOneOnOne: false,
      hasWorkshop: false,
      hasCheckins: checkinCount > 0 || checkinParticipantTotal > 0,
    };
    cohort.modalities?.forEach((m) => {
      if (m.modality_type === 'group_coaching') result.hasGroupCoaching = true;
      if (m.modality_type === 'one_on_one') result.hasOneOnOne = true;
      if (m.modality_type === 'workshop') result.hasWorkshop = true;
    });
    return result;
  }, [cohort.modalities, checkinCount, checkinParticipantTotal]);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const hasOneOnOne = cohort.modalities?.some((m) => m.modality_type === 'one_on_one');
      const hasCheckins = checkinCount > 0;
      // MED-1350: a group coaching cohort can keep a booked check-in after both
      // toggles go off, so its 1:1 list loads too. The list meta says whether
      // any live check-in row is left.
      const hasGroupCoaching = cohort.modalities?.some((m) => m.modality_type === 'group_coaching');

      const [groupsData, sessionsData, timeSlotsData, finalizationData, sessionTopicsData, huddlesData, oneOnOneData] =
        await Promise.all([
          groupsApi.list(cohort.id),
          sessionsApi.listWithRsvp(cohort.id).catch(() => sessionsApi.list(cohort.id)),
          timeSlotsApi.list(cohort.id).catch(() => ({ time_slots: [], total: 0 })),
          sessionsApi.getFinalizationStatus(cohort.id).catch(() => null),
          topicsApi.getCohortSessionTopics(cohort.id).catch(() => []),
          sessionsApi.getCohortHuddles(cohort.id).catch(() => [] as CoachHuddle[]),
          hasOneOnOne || hasCheckins || hasGroupCoaching
            ? sessionsApi
                .listOneOnOneParticipants(cohort.id, { page: 1, limit: 25 })
                .catch(() => ({ data: [] as OneOnOneParticipant[], pagination: { page: 1, limit: 25, total: 0, totalPages: 0 } }))
            : Promise.resolve({ data: [] as OneOnOneParticipant[], pagination: { page: 1, limit: 25, total: 0, totalPages: 0 } }),
        ]);

      const groupsList = groupsData.data ?? [];
      const sessionsList = Array.isArray(sessionsData) ? sessionsData : [];

      setGroups(groupsList);
      setAllSessions(sessionsList);
      setTimeSlots(timeSlotsData.time_slots || []);
      setFinalizationStatus(finalizationData);
      setSessionTopics(Array.isArray(sessionTopicsData) ? sessionTopicsData : []);
      setCohortHuddles(Array.isArray(huddlesData) ? huddlesData : []);
      setOneOnOneParticipants(oneOnOneData.data);
      setOneOnOneTotal(oneOnOneData.pagination.total);
      setOneOnOnePaidParticipantTotal(
        'paid_participant_total' in oneOnOneData.pagination
          ? (oneOnOneData.pagination.paid_participant_total ?? 0)
          : 0,
      );
      setCheckinParticipantTotal(
        'checkin_participant_total' in oneOnOneData.pagination
          ? (oneOnOneData.pagination.checkin_participant_total ?? 0)
          : 0,
      );
      setOneOnOnePage(1);

      // Auto-expand all groups initially
      setExpandedGroups(new Set(groupsList.map((g: GroupWithMembers) => g.id)));
    } catch {
      // Silently handle errors - empty state will show
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [cohort.id, cohort.modalities, checkinCount]);

  // Fetch when tab becomes active or when refreshKey changes
  useEffect(() => {
    if (isActive) {
      loadData();
      if (refreshKey) {
        setCohortEventsKey((prev) => prev + 1);
      }
    }
  }, [isActive, loadData, refreshKey]);

  // Organize sessions by group, sorted naturally (Group 1, Group 2, ..., Group 10)
  const groupsWithSessions = useMemo((): GroupWithSessions[] => {
    return groups.map((group) => {
      const groupSessions = allSessions.filter((s) => s.group_id === group.id);
      const firstSession = groupSessions[0];
      const scheduledTimeSlot = firstSession?.time_slot_identifier
        ? timeSlots.find((ts) => ts.identifier === firstSession.time_slot_identifier)
        : undefined;

      const finalizedInfo = finalizationStatus?.finalized_groups.find((g) => g.id === group.id);
      const isFinalized = !!finalizedInfo;

      return {
        ...group,
        sessions: groupSessions.sort(
          (a, b) =>
            new Date(a.scheduled_start || 0).getTime() - new Date(b.scheduled_start || 0).getTime()
        ),
        scheduledTimeSlot,
        isFinalized,
        // MED-838: Server-side unique pending count to drive the resend-invites callout.
        pendingUniqueCount: finalizedInfo?.pending_unique_count,
      };
    }).sort((a, b) => naturalSortCompare(a.name, b.name));
  }, [groups, allSessions, timeSlots, finalizationStatus]);

  // Filter sessions by modality type for tab counts
  const sessionCounts = useMemo((): SessionCounts => {
    const oneOnOneModality = cohort.modalities?.find((m) => m.modality_type === 'one_on_one');

    if (groups.length > 0) {
      // Count distinct session_numbers for group coaching — not total rows across all groups
      // e.g., 4 groups × 6 sessions = 24 rows, but only 6 unique session numbers
      const groupSessionNumbers = new Set<number>();
      allSessions.forEach((session) => {
        if (
          session.group_id &&
          (session.session_type === 'group_session' || !session.session_type) &&
          session.session_number != null
        ) {
          groupSessionNumbers.add(session.session_number);
        }
      });
      const groupCoachingCount = groupSessionNumbers.size;

      // Workshop sessions have group_id = null — count from allSessions directly
      const workshopCount = allSessions.filter(
        (s) => s.session_type === 'workshop_session',
      ).length;

      // 1:1 count is the configured sessions per participant from the modality,
      // plus the check-ins (MED-1350)
      const oneOnOneCount = (oneOnOneModality?.session_count ?? 0) + checkinCount;

      return {
        all: groupCoachingCount + oneOnOneCount + workshopCount,
        groupCoaching: groupCoachingCount,
        oneOnOne: oneOnOneCount,
        workshop: workshopCount,
      };
    }

    // Zero state: no groups/sessions created yet — show actual counts to match nav header
    // Group coaching and workshops are 0 until sessions are generated
    // 1:1 uses modality config (sessions per participant) plus the check-ins
    // that are on, to match nav header SQL (MED-1350: the API adds them too)
    const oneOnOneCount = (oneOnOneModality?.session_count ?? 0) + checkinCount;

    return {
      all: oneOnOneCount,
      groupCoaching: 0,
      oneOnOne: oneOnOneCount,
      workshop: 0,
    };
  }, [allSessions, groups.length, cohort.modalities, checkinCount]);

  // Filter groups based on active tab, group filter, and coach filter
  const filteredGroups = useMemo((): GroupWithSessions[] => {
    let filtered = groupsWithSessions;

    if (groupFilter.length > 0) {
      filtered = filtered.filter((g) => groupFilter.includes(g.id));
    }

    if (coachFilter.length > 0) {
      filtered = filtered.filter((g) => g.coach_name && coachFilter.includes(g.coach_name));
    }

    if (activeTab === 'all' || activeTab === 'groupCoaching') {
      return filtered;
    }

    return [];
  }, [activeTab, groupFilter, coachFilter, groupsWithSessions]);

  // Toggle group expansion
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // Expand/collapse all groups
  const expandAllGroups = useCallback(() => {
    setExpandedGroups(new Set(groups.map((g) => g.id)));
  }, [groups]);

  const collapseAllGroups = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  const allGroupsExpanded = groups.length > 0 && expandedGroups.size === groups.length;

  // Handle topic change for zero-state sessions
  const handleZeroStateTopicChange = useCallback(
    (sessionNumber: number, topicId: string, topicName: string) => {
      setSessionTopics((prev) => {
        const existing = prev.find((t) => t.session_number === sessionNumber);
        if (existing) {
          return prev.map((t) =>
            t.session_number === sessionNumber ? { ...t, topic_id: topicId, topic_name: topicName } : t
          );
        }
        return [
          ...prev,
          {
            id: `temp-${sessionNumber}`,
            cohort_id: cohort.id,
            session_number: sessionNumber,
            topic_id: topicId,
            topic_name: topicName,
            // MED-832: optimistic-update placeholder; real row from server will
            // carry whatever display_name the user actually set (or null).
            display_name: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ];
      });
    },
    [cohort.id]
  );

  // Handle topic select for existing session.
  //
  // MED-1190 (product decision 2026-07-13): the sessions-page pencil modal and
  // the Content Editor's "Session Topic" field are two doors to the SAME value.
  // Both write the cohort-wide session-number topic (`session_number_config.topic`)
  // via `setGroupCoachingTopic` — the identical call the Content Editor makes —
  // so editing the topic in either place stays in sync and updates the other
  // (AC2, both directions). This retires the previous per-group override model
  // (MED-851 `sessions.topic_id` decoupling / `sessions.topic_override`): there
  // is now one topic per session number, applied to every group's session for
  // that number. Editing one group's topic therefore updates them all — that is
  // the intended behavior per product.
  //
  // MED-832: a participant-facing `display_name` override is a SEPARATE label
  // (not the topic itself) and still persists on `cohort_session_topics` for the
  // session number, using the existing canonical topic_id when one exists so a
  // single-group edit doesn't clobber the cohort-wide canonical.
  const handleTopicSelectForSession = useCallback(
    async (
      topic: Topic,
      session: SessionWithDetailsAndRsvp,
      displayName?: string | null,
    ) => {
      if (session.session_number === null || session.session_number === undefined) return;
      try {
        await sessionNumberConfigsApi.setGroupCoachingTopic(
          cohort.id,
          session.session_number,
          topic.name,
        );

        if (displayName !== undefined) {
          const existingCanonical = sessionTopics.find(
            (t) => t.session_number === session.session_number,
          );
          const canonicalTopicId = existingCanonical?.topic_id ?? topic.id;
          await topicsApi.assignSessionTopic(
            cohort.id,
            session.session_number,
            canonicalTopicId,
            displayName,
          );
        }

        loadData();
      } catch {
        // Error handling
      }
    },
    [cohort.id, loadData, sessionTopics],
  );

  // Handle adding a new coach huddle
  const handleAddHuddle = useCallback(
    async (date: string | null) => {
      try {
        await cohortEventsApi.createHuddle(cohort.id, date);
        setCohortEventsKey((prev) => prev + 1);
      } catch {
        // Error handling
      }
    },
    [cohort.id]
  );

  const handleCohortEventRefresh = useCallback(() => {
    setCohortEventsKey((prev) => prev + 1);
  }, []);

  const loadMoreOneOnOne = useCallback(async () => {
    if (loadingMoreOneOnOne) return;
    setLoadingMoreOneOnOne(true);
    try {
      const nextPage = oneOnOnePage + 1;
      const result = await sessionsApi.listOneOnOneParticipants(cohort.id, { page: nextPage, limit: 25 });
      setOneOnOneParticipants((prev) => [...prev, ...result.data]);
      setOneOnOneTotal(result.pagination.total);
      setOneOnOnePage(nextPage);
    } finally {
      setLoadingMoreOneOnOne(false);
    }
  }, [cohort.id, oneOnOnePage, loadingMoreOneOnOne]);

  return {
    groups,
    allSessions,
    sessionTopics,
    timeSlots,
    finalizationStatus,
    cohortHuddles,
    oneOnOneParticipants,
    oneOnOneTotal,
    oneOnOnePaidParticipantTotal,
    groupsWithSessions,
    modalities,
    sessionCounts,
    filteredGroups,
    loading,
    hasLoaded,
    expandedGroups,
    showOneOnOnes,
    loadingMoreOneOnOne,
    loadData,
    loadMoreOneOnOne,
    toggleGroup,
    expandAllGroups,
    collapseAllGroups,
    allGroupsExpanded,
    setShowOneOnOnes,
    setSessionTopics,
    handleZeroStateTopicChange,
    handleTopicSelectForSession,
    handleAddHuddle,
    handleCohortEventRefresh,
    cohortEventsKey,
  };
}
